import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';

import { useAuth, UserRole } from '../contexts/AuthContext';

const APP_VERSION = import.meta.env.VITE_APP_VERSION;

interface NavItem {
  label: string;
  path: string;
  roles: UserRole[];
  icon?: string;
}

const navigationItems: NavItem[] = [
  // Admin-only routes
  {
    label: 'User Management',
    path: '/dashboard/users',
    roles: ['admin'],
    icon: '👥',
  },
  {
    label: 'System Settings',
    path: '/dashboard/settings',
    roles: ['admin'],
    icon: '⚙️',
  },

  // Coach routes
  {
    label: 'My Clients',
    path: '/dashboard/clients',
    roles: ['coach'],
    icon: '👤',
  },
  {
    label: 'Assessments',
    path: '/dashboard/assessments',
    roles: ['coach', 'admin'],
    icon: '📋',
  },

  // User routes
  {
    label: 'My Health',
    path: '/dashboard/health',
    roles: ['user', 'coach', 'admin'],
    icon: '❤️',
  },
  {
    label: 'My Metrics',
    path: '/dashboard/metrics',
    roles: ['user', 'coach', 'admin'],
    icon: '📊',
  },
  { label: 'My Coach', path: '/dashboard/coach', roles: ['user'], icon: '🎯' },

  // Shared routes
  {
    label: 'Profile',
    path: '/dashboard/profile',
    roles: ['user', 'coach', 'admin'],
    icon: '👤',
  },
];

export function DashboardLayout() {
  const { user, signOut, hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const hamburgerButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const originalOverflowRef = useRef<string>('');

  async function handleSignOut() {
    await signOut();
    navigate('/signin');
  }

  function toggleMobileMenu() {
    setIsMobileMenuOpen((prev) => !prev);
  }

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Filter navigation items based on user's roles
  const visibleNavItems = navigationItems.filter((item) =>
    hasAnyRole(item.roles)
  );

  // Get user's primary role for display (highest precedence)
  const primaryRole = user?.roles.includes('admin')
    ? 'Admin'
    : user?.roles.includes('coach')
      ? 'Coach'
      : 'User';

  // Close mobile menu when navigating to a new page
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  // Close mobile menu when pressing Escape key
  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        closeMobileMenu();
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isMobileMenuOpen, closeMobileMenu]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    // Capture original overflow value on mount
    if (originalOverflowRef.current === '') {
      originalOverflowRef.current = document.body.style.overflow;
    }

    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = originalOverflowRef.current;
    }

    return () => {
      document.body.style.overflow = originalOverflowRef.current;
    };
  }, [isMobileMenuOpen]);

  // Focus management for drawer
  useEffect(() => {
    if (isMobileMenuOpen && drawerRef.current) {
      // Focus the drawer when it opens
      drawerRef.current.focus();
    } else if (!isMobileMenuOpen && hamburgerButtonRef.current) {
      // Return focus to hamburger button when drawer closes
      hamburgerButtonRef.current.focus();
    }
  }, [isMobileMenuOpen]);

  // Trap focus within drawer when open
  useEffect(() => {
    if (!isMobileMenuOpen || !drawerRef.current) return;

    const drawer = drawerRef.current;
    const focusableElements = drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0] as HTMLElement;
    const lastFocusable = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    function handleTabKey(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    }

    drawer.addEventListener('keydown', handleTabKey);
    return () => {
      drawer.removeEventListener('keydown', handleTabKey);
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="h-screen flex bg-cream">
      {/* Hamburger Menu Button - Mobile Only */}
      <button
        ref={hamburgerButtonRef}
        onClick={toggleMobileMenu}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-md hover:bg-gray-50 transition-colors"
        aria-label="Open menu"
      >
        <svg
          className="w-6 h-6 text-gray-900"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      {/* Backdrop Overlay - Mobile Only */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      {/* Mobile Drawer - Only render when open */}
      {isMobileMenuOpen && (
        <aside
          ref={drawerRef}
          className="fixed top-0 left-0 h-screen w-64 shadow-lg bg-white z-50 lg:hidden flex flex-col"
          role="dialog"
          aria-label="Mobile navigation menu"
          aria-modal="true"
          tabIndex={-1}
        >
          {/* Close Button */}
          <button
            onClick={closeMobileMenu}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close menu"
          >
            <svg
              className="w-6 h-6 text-gray-900"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Logo/Brand */}
          <Link
            to="/dashboard"
            className="block p-6 border-b border-gray-200 hover:bg-gray-50 transition-colors flex-shrink-0"
            onClick={closeMobileMenu}
          >
            <h1 className="text-xl font-bold text-primary">
              Metabolic Health Coach
            </h1>
            <p className="text-sm mt-1 text-text-secondary">
              {primaryRole} Dashboard
            </p>
          </Link>

          {/* Navigation - scrollable if needed */}
          <nav className="p-4 flex-1 min-h-0 overflow-y-auto">
            {visibleNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
                    isActive
                      ? 'bg-beige text-primary font-semibold'
                      : 'hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <span className="text-xl">{item.icon}</span>
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User info & sign out - fixed at bottom */}
          <div className="p-4 border-t border-gray-200 flex-shrink-0">
            <div className="mb-3">
              <p className="text-sm font-medium truncate text-text-dark">
                {user?.email}
              </p>
              <p className="text-xs text-text-secondary">v{APP_VERSION}</p>
            </div>
            <button
              onClick={handleSignOut}
              className="w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-beige text-text-dark hover:bg-gray-200"
            >
              Sign Out
            </button>
          </div>
        </aside>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 shadow-lg bg-white flex-col h-screen">
        {/* Logo/Brand */}
        <Link
          to="/dashboard"
          className="block p-6 border-b border-gray-200 hover:bg-gray-50 transition-colors flex-shrink-0"
        >
          <h1 className="text-xl font-bold text-primary">
            Metabolic Health Coach
          </h1>
          <p className="text-sm mt-1 text-text-secondary">
            {primaryRole} Dashboard
          </p>
        </Link>

        {/* Navigation - scrollable if needed */}
        <nav className="p-4 flex-1 min-h-0 overflow-y-auto">
          {visibleNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
                  isActive
                    ? 'bg-beige text-primary font-semibold'
                    : 'hover:bg-gray-50 text-gray-900'
                }`}
              >
                <span className="text-xl">{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User info & sign out - fixed at bottom */}
        <div className="p-4 border-t border-gray-200 flex-shrink-0">
          <div className="mb-3">
            <p className="text-sm font-medium truncate text-text-dark">
              {user?.email}
            </p>
            <p className="text-xs text-text-secondary">v{APP_VERSION}</p>
          </div>
          <button
            onClick={handleSignOut}
            className="w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-beige text-text-dark hover:bg-gray-200"
          >
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content area */}
      {/*
        Mobile padding (pt-14 pl-14 = 56px) provides clearance for hamburger button:
        - Button position: top-4 left-4 (16px offset)
        - Button size: w-6 h-6 (24px) + p-2 (8px padding each side) = 40px total
        - Required clearance: 16px + 40px = 56px
        Desktop: padding removed (lg:pt-0 lg:pl-0) as sidebar occupies left space
      */}
      <main className="flex-1 overflow-auto pt-14 pl-14 lg:pt-0 lg:pl-0">
        <Outlet />
      </main>
    </div>
  );
}
